/**
 * Registration desk ("Mesa de registro", /registro/:eventId): one screen for on-site work.
 * Search by CI (the number alone is enough) → the person's card shows what is missing
 * (verify → charge → receipt → credential) and does each step with one button.
 * Receipts always open as the 80 mm thermal roll; credentials as the sticker label.
 */

import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Search, ShieldAlert, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/supabase.config';
import { useAuth } from '@/context/authContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/use-toast';
import { getEventPaymentQrUrl } from '@/Services/events.service';
import { getReceiptById } from '@/features/receipts/data/receiptsRepository';
import { ReceiptPrintPreview } from '@/features/receipts/components/ReceiptPrintPreview';
import { receiptToPrintData, type Receipt } from '@/features/receipts/types';
import { publicReceiptUrl } from '@/features/receipts/utils/publicReceiptUrl';
import { BadgeStickerPrint, type BadgeStickerData } from '../../print/BadgeStickers';
import { DeskGuestDialog } from '../components/DeskGuestDialog';
import { DeskDelegateDialog } from '../components/DeskDelegateDialog';
import { DeskMinisterCard, DeskParticipantCard } from '../components/DeskCards';
import { lookupDesk, markAttended } from '../deskRepository';
import { useEventHasVoting } from '../../hooks/useEventHasVoting';
import type { DeskKind, DeskParticipant, DeskPerson } from '../deskLogic';

const MIN_CI_LENGTH = 5;

export function RegistrationDeskPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const location = useLocation();
  const { can, isLoading: permsLoading } = usePermissions();

  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  if (permsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!can('event_participant.register')) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <ShieldAlert className="size-10 text-muted-foreground" />
        <p className="text-muted-foreground">Su usuario no tiene permiso para registrar participantes.</p>
      </div>
    );
  }
  return <DeskScreen eventId={eventId as string} canCharge={can('event_payment.record')} />;
}

function DeskScreen({ eventId, canCharge }: { eventId: string; canCharge: boolean }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');
  const [ci, setCi] = useState('');
  const [sticker, setSticker] = useState<BadgeStickerData | null>(null);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [guestOpen, setGuestOpen] = useState(false);
  const [delegateOpen, setDelegateOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const hasVoting = useEventHasVoting(eventId);

  // Debounce typing
  useEffect(() => {
    const t = setTimeout(() => setCi(input.trim()), 400);
    return () => clearTimeout(t);
  }, [input]);

  const { data: event } = useQuery({
    queryKey: ['desk_event', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select(
          'id, code, name, status, allow_public_guests, registration_form_type, registration_declaration, signature_mode, payment_qr_filename, payment_instructions',
        )
        .eq('id', eventId)
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const searching = ci.replace(/[\s.-]/g, '').length >= MIN_CI_LENGTH;
  const { data: rows = [], isFetching, error } = useQuery({
    queryKey: ['desk_lookup', eventId, ci],
    queryFn: () => lookupDesk(eventId, ci),
    enabled: searching,
  });

  const eventOpen = event?.status === 'OPEN';
  const paymentInfo = {
    qrUrl: getEventPaymentQrUrl(event?.payment_qr_filename),
    instructions: event?.payment_instructions ?? null,
  };
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['desk_lookup', eventId] });
  const guestsAllowed = event?.allow_public_guests === true;

  /** Next person: clear everything and put the cursor back in the search box */
  const startNew = () => {
    setInput('');
    setCi('');
    setSticker(null);
    setReceipt(null);
    setGuestOpen(false);
    setDelegateOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    searchRef.current?.focus();
  };

  const printReceipt = async (receiptId: string) => {
    try {
      setReceipt(await getReceiptById(receiptId));
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'No se pudo abrir el recibo', variant: 'destructive' });
    }
  };

  /** Handing over the credential marks the person as present, then opens the sticker */
  const deliverCredential = async (person: DeskPerson, kind: DeskKind, participant: DeskParticipant) => {
    if (participant.attendance_status !== 'ATTENDED') {
      try {
        await markAttended(participant.id);
        refresh();
      } catch (err) {
        // Don't block the desk: the sticker still prints, attendance can be marked from the participants list
        toast({
          title: 'No se pudo registrar la asistencia',
          description: err instanceof Error ? err.message : undefined,
          variant: 'destructive',
        });
      }
    }
    setSticker({
      token: participant.public_token,
      full_name: person.full_name ?? '',
      dni: person.dni,
      adb_code: person.adb_code,
      district: person.district_name,
      region: person.region_name,
      kind,
      voting_pin: hasVoting ? participant.voting_pin : null,
    });
  };

  return (
    <div className="min-h-screen bg-muted/40">
      <header className="sticky top-0 z-10 space-y-3 border-b bg-background px-4 py-3">
        <div>
          <p className="truncate font-semibold">{event?.name ?? 'Cargando…'}</p>
          <p className="text-xs text-muted-foreground">Mesa de registro</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchRef}
            autoFocus
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Número de CI (ej. 1234567)"
            className="h-14 pl-11 text-lg"
            autoComplete="off"
            inputMode="text"
          />
          {isFetching && <Loader2 className="absolute right-3 top-1/2 size-5 -translate-y-1/2 animate-spin text-muted-foreground" />}
        </div>
        <p className="text-center text-xs text-muted-foreground">Basta el número de CI, sin extensión.</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button
            variant="outline"
            size="lg"
            className="h-12 flex-1 text-base sm:min-w-52 sm:flex-none"
            onClick={() => setDelegateOpen(true)}
            disabled={!eventOpen}
          >
            <UserPlus className="mr-2 size-5" /> Registrar delegado
          </Button>
          {guestsAllowed && (
            <Button
              variant="outline"
              size="lg"
              className="h-12 flex-1 text-base sm:min-w-52 sm:flex-none"
              onClick={() => setGuestOpen(true)}
              disabled={!eventOpen}
            >
              <UserPlus className="mr-2 size-5" /> Registrar invitado
            </Button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 p-4">
        {event && !eventOpen && (
          <p className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="size-4 shrink-0" />
            El evento no está abierto a inscripciones: solo puede cobrar, emitir recibos e imprimir credenciales de personas ya inscritas.
          </p>
        )}

        {!searching && <p className="py-8 text-center text-muted-foreground">Escriba el CI de la persona para empezar.</p>}

        {error && <p className="text-sm text-destructive">{error instanceof Error ? error.message : 'Error al buscar'}</p>}

        {searching && !isFetching && rows.length === 0 && !error && (
          <p className="rounded-lg border bg-card p-4 text-center text-sm text-muted-foreground">
            No hay ministros ni inscritos con ese CI. Revise el número o use «Registrar delegado»
            {guestsAllowed ? ' / «Registrar invitado»' : ''} arriba.
          </p>
        )}

        {rows.map((row) =>
          row.source === 'MINISTER' ? (
            <DeskMinisterCard
              key={`m-${row.person.minister_id}`}
              person={row.person}
              pricing={row.pricing}
              contact={row.contact}
              event={{
                registration_form_type: event?.registration_form_type ?? null,
                registration_declaration: event?.registration_declaration ?? null,
                signature_mode: event?.signature_mode ?? null,
              }}
              eventId={eventId}
              eventOpen={eventOpen}
              onChanged={refresh}
            />
          ) : (
            <DeskParticipantCard
              key={row.participant.id}
              person={row.person}
              kind={row.kind}
              participant={row.participant}
              eventId={eventId}
              eventName={event?.name ?? ''}
              paymentInfo={paymentInfo}
              eventOpen={eventOpen}
              canCharge={canCharge}
              onChanged={refresh}
              onPrintReceipt={printReceipt}
              onDeliverCredential={deliverCredential}
              onNewParticipant={startNew}
            />
          ),
        )}
      </main>

      {guestOpen && (
        <DeskGuestDialog
          eventId={eventId}
          initialDni={ci}
          open={guestOpen}
          onOpenChange={setGuestOpen}
          onRegistered={refresh}
        />
      )}
      {delegateOpen && (
        <DeskDelegateDialog
          eventId={eventId}
          initialDni={ci}
          declarationText={event?.registration_declaration}
          signatureRequired={event?.signature_mode === 'DIGITAL'}
          open={delegateOpen}
          onOpenChange={setDelegateOpen}
          onRegistered={refresh}
        />
      )}
      {sticker && <BadgeStickerPrint stickers={[sticker]} onClose={() => setSticker(null)} />}
      {receipt && (
        <ReceiptPrintPreview
          data={receiptToPrintData(receipt, publicReceiptUrl(receipt.public_token))}
          onClose={() => setReceipt(null)}
        />
      )}
    </div>
  );
}
